import React, { useEffect, useRef, forwardRef } from 'react';

export interface AutoResizeTextareaProps {
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  className?: string;
  style?: React.CSSProperties;
  minRows?: number;
  maxRows?: number;
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement>;
  onFocus?: React.FocusEventHandler<HTMLTextAreaElement>;
  placeholder?: string;
  readOnly?: boolean;
  maxLength?: number;
  onCompositionStart?: any;
  onCompositionEnd?: any;
  disabled?: boolean;
}

const AutoResizeTextarea = forwardRef<HTMLTextAreaElement, AutoResizeTextareaProps>(
  (
    {
      value,
      onChange,
      className,
      style,
      minRows = 1,
      maxRows = 3,
      onBlur,
      onFocus,
      placeholder,
      readOnly,
      maxLength,
      onCompositionStart,
      onCompositionEnd,
      disabled = false,
    },
    ref
  ) => {
    const textareaRef = useRef<HTMLTextAreaElement>();

    // 合并ref
    const mergedRef = (node: HTMLTextAreaElement) => {
      textareaRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };

    useEffect(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        const lineHeight = parseInt(window.getComputedStyle(textareaRef.current).lineHeight, 10);
        const minHeight = lineHeight * minRows;
        const maxHeight = lineHeight * maxRows;

        textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;

        if (textareaRef.current.scrollHeight > maxHeight) {
          textareaRef.current.style.overflowY = 'scroll';
          textareaRef.current.style.height = `${maxHeight}px`;
        } else {
          textareaRef.current.style.overflowY = 'hidden';
          if (textareaRef.current.scrollHeight < minHeight) {
            textareaRef.current.style.height = `${minHeight}px`;
          }
        }
      }
    }, [value, minRows, maxRows]);

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (maxRows === 1) {
        const filteredValue = event.target.value.replace(/\n/g, '');
        event.target.value = filteredValue;
      }
      onChange?.(event);
    };

    const handleCompositionStart = (event: React.CompositionEvent<HTMLTextAreaElement>) => {
      onCompositionStart?.(event);
    };

    const handleCompositionEnd = (event: React.CompositionEvent<HTMLTextAreaElement>) => {
      onCompositionEnd?.(event);
    };

    return (
      <textarea
        ref={mergedRef}
        value={value}
        onChange={handleChange}
        onBlur={onBlur}
        className={className}
        style={{ ...style }}
        rows={minRows}
        placeholder={placeholder}
        readOnly={readOnly}
        maxLength={maxLength}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
        onFocus={onFocus}
        disabled={disabled}
      />
    );
  }
);

AutoResizeTextarea.displayName = 'AutoResizeTextarea';

export { AutoResizeTextarea };
